/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {
  GeofencesDrawIcon
} from '../../icons/GeofencesDrawIcon';
import {
  GeofencesPolygonIcon
} from '../../icons/GeofencesPolygonIcon';
import {
  GeofencesRadialIcon
} from '../../icons/GeofencesRadialIcon';
import {
  MaterialBackIcon
} from '../../icons/MaterialBackIcon';
import {GeofenceOptions} from '../../maps/geofences/GeofencesList';
import {CreateGeoFenceTypeEnum, DLSTheme} from '../../types/types';
import {HeaderTypography} from '../../typography/HeaderTypography';
import {TertiaryButton} from '../buttons/TertiaryButton';

interface BoundaryFilterToolsProps {
  handleExitBoundaryFilter: () => void;
  onClearClick: () => void;
  onBoundaryChoose: Function;
  setMenuOpen: Function;
  disableClear: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  createGeoFenceHeader : {
    display        : 'flex',
    justifyContent : 'space-between',
    alignItems     : 'center'
  },
  mobileHeader : {
    display      : 'flex',
    alignItems   : 'center',
    marginBottom : '15px'
  },
  label : {
    ...dlsAlt('typography', 'label1'),
    color : dlsColor('grey3')
  }
}));

/**
 * BoundaryFilterTools will give option to create a new boundary including a radial,
 * polygon, or draw.
 *
 * ```typescript
 *
 * import {BoundaryFilterTools} from '@ion/dls/src/components/menus';
 *
 * <BoundaryFilterTools ... />
 * ```
 */

export const BoundaryFilterTools = ({
  handleExitBoundaryFilter,
  onClearClick,
  onBoundaryChoose,
  disableClear,
  setMenuOpen
}: BoundaryFilterToolsProps) => {
  const {isPhone} = useMedia();
  const classes   = useStyles();

  const handleAction = useCallback((type: CreateGeoFenceTypeEnum) => {
    onBoundaryChoose(type);
    setMenuOpen(false);
  }, []);

  return (
    <>
      <div className={classes.mobileHeader}>
        {
          isPhone && (
            <>
              <div onClick={handleExitBoundaryFilter}>
                <MaterialBackIcon />
              </div>
              <span className={classes.label}>{translate('common:filters.boundary')}</span>
            </>
          )
        }
      </div>
      <div className={classes.createGeoFenceHeader}>
        <HeaderTypography variant='h4'>
          {translate('maps:boundary.createBoundary')}
        </HeaderTypography>
        <TertiaryButton disabled={disableClear} onClick={onClearClick}>
          {translate('maps:boundary.clear')}
        </TertiaryButton>
      </div>
      <GeofenceOptions title={translate('maps:geofences.radial')}
        description={translate('maps:boundary.radialDesc')}
        icon={<GeofencesRadialIcon />}
        onClick={function() {
          handleAction(CreateGeoFenceTypeEnum.Radial);
        }} />
      <GeofenceOptions title={translate('maps:geofences.polygon')}
        description={translate('maps:boundary.polygonDesc')}
        icon={<GeofencesPolygonIcon />}
        onClick={function() {
          handleAction(CreateGeoFenceTypeEnum.Polygon);
        }} />
      <GeofenceOptions title={translate('maps:geofences.draw')}
        description={translate('maps:boundary.drawDesc')}
        icon={<GeofencesDrawIcon size={40} />}
        onClick={function() {
          handleAction(CreateGeoFenceTypeEnum.Draw);
        }} />
    </>
  );
};

export default memo(BoundaryFilterTools);
